import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle2, TrendingUp, AlertTriangle, ArrowRight, RotateCcw, Download, Wrench, ArrowUpRight, Circle } from "lucide-react";
import { PLAYBOOK_STEPS, PLAYBOOK_PDF_URL, getStep } from "@/data/playbook";
import { useProgress } from "@/hooks/useProgress";
import StrategyCallDialog from "@/components/StrategyCallDialog";
import { STRATEGIST } from "@/data/strategist";

function useCountUp(target, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setVal(Math.round((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

const DRIVER = "https://images.unsplash.com/photo-1695395860103-38d172403a46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxjb25maWRlbnQlMjB0cnVjayUyMGRyaXZlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4Mzg0NTAwMXww&ixlib=rb-4.1.0&q=85";

export default function Result({ result, onRestart, api }) {
  const { score, tier, ai_analysis, category_scores, weakSteps = [] } = result;
  const { isDone, toggle } = useProgress();
  const count = useCountUp(score);
  const qualified = tier === "qualified";
  const scoreColor = score >= 70 ? "#10B981" : score >= 45 ? "#F59E0B" : "#DC2626";
  const fixSteps = weakSteps.map(getStep).filter(Boolean).sort((a, b) => a.n - b.n);

  return (
    <div className="bg-white">
      {/* Score reveal */}
      <section className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white text-center px-8 py-20">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-slate-500 mb-6">
            Your freight readiness score
          </div>
          <div className="font-mono font-bold tabular leading-none" style={{ fontSize: "clamp(6rem, 22vw, 14rem)", color: scoreColor }}>
            {count}
          </div>
          <div className="text-slate-500 font-mono text-lg -mt-2">/ 100</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-8 max-w-2xl mx-auto" data-testid="result-headline">
            {ai_analysis.headline}
          </h2>
          <div className={`inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full text-sm font-bold ${qualified ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
            {qualified ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {qualified ? "Qualified to delegate & scale" : "Foundation-building recommended"}
          </div>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.6 }}
            className="mt-16 text-slate-500 text-xs tracking-[0.2em] uppercase"
          >
            Scroll for your plan ↓
          </motion.div>
        </motion.div>
      </section>

      {/* Analysis */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <p className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 leading-snug max-w-3xl" data-testid="result-summary">
          {ai_analysis.summary}
        </p>

        {/* Category bars */}
        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-5 mt-12 max-w-3xl">
          {Object.entries(category_scores).map(([cat, val]) => (
            <div key={cat}>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-semibold text-slate-800">{cat}</span>
                <span className="font-mono tabular text-slate-500">{val}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${val}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="h-full rounded-full"
                  style={{ background: val >= 70 ? "#10B981" : val >= 45 ? "#F59E0B" : "#DC2626" }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-16">
          <div className="border border-slate-200 rounded-md p-8">
            <div className="flex items-center gap-2 text-emerald-600 mb-4">
              <TrendingUp className="w-5 h-5" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">Your strengths</h3>
            </div>
            <ul className="space-y-3">
              {ai_analysis.strengths.map((s, i) => (
                <li key={i} className="flex gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-slate-200 rounded-md p-8">
            <div className="flex items-center gap-2 text-amber-600 mb-4">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">Gaps to close</h3>
            </div>
            <ul className="space-y-3">
              {ai_analysis.gaps.map((s, i) => (
                <li key={i} className="flex gap-3 text-slate-700">
                  <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Branched outcome */}
      {qualified ? (
        <section className="bg-slate-900 text-white">
          <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-4">Recommended path · Delegate</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">
                You're ready. Let's map your most profitable lanes.
              </h2>
              <p className="mt-5 text-slate-300 leading-relaxed">
                {STRATEGIST.name}, {STRATEGIST.title.toLowerCase()} at {STRATEGIST.business}, will help you turn this
                readiness into profitable miles — lane selection, rate strategy and cash flow. Book a session to map your first 90 days.
              </p>
              <ul className="mt-8 space-y-3">
                {ai_analysis.next_steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-slate-200">
                    <ArrowRight className="w-5 h-5 text-brand shrink-0 mt-0.5" /> {s}
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <StrategyCallDialog source="strategy_diagnostic_qualified" triggerClassName="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold" />
              </div>
            </div>
            <div className="relative">
              <img src={DRIVER} alt="Confident truck driver" className="w-full h-[420px] object-cover rounded-md border border-white/10" />
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-slate-50 border-t border-slate-200">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-4">Recommended path · Self-correct</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-slate-900 max-w-2xl">
              Build your foundation with the 12-Step Business Foundation Guide.
            </h2>
            <p className="mt-5 text-slate-600 max-w-2xl leading-relaxed">
              You're closer than you think. Work through RouteHaul's 12-step system to close your gaps —
              then re-run the diagnostic when you're ready to scale.
            </p>

            <a
              href={PLAYBOOK_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-playbook-button"
              className="inline-flex items-center gap-2 mt-6 h-12 px-6 rounded-full bg-brand hover:bg-brand-hover text-white text-sm font-bold transition-colors"
            >
              <Download className="w-4 h-4" /> Download the full Guide (PDF)
            </a>

            {fixSteps.length > 0 && (
              <div className="mt-12" data-testid="priority-fixes">
                <div className="flex items-center gap-2 mb-2">
                  <Wrench className="w-5 h-5 text-brand" />
                  <h3 className="font-heading text-xl font-semibold text-slate-900">Fix your biggest risks first</h3>
                </div>
                <p className="text-slate-600 mb-6 max-w-2xl">
                  Based on your answers, these are the steps most likely to trip you up. Here's what goes wrong — and where to fix it.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {fixSteps.map((s) => (
                    <div key={s.n} data-testid={`fix-card-${s.n}`} className={`bg-white border rounded-md p-6 flex flex-col ${isDone(s.n) ? "border-emerald-300" : "border-slate-200"}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs tracking-[0.15em] uppercase font-bold text-slate-400">Step {String(s.n).padStart(2, "0")} · {s.tag}</span>
                      </div>
                      <h4 className="font-heading text-lg font-semibold text-slate-900 mb-3">{s.title}</h4>
                      <div className="border-l-4 border-amber-500 bg-amber-50 rounded-md p-4 mb-4">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                          <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-amber-700">What goes wrong</span>
                        </div>
                        <p className="text-sm text-amber-900 leading-relaxed">{s.note}</p>
                      </div>
                      <div className="mt-auto flex items-center justify-between gap-3 pt-1">
                        <Link
                          to={`/step/${s.n}#pitfall`}
                          data-testid={`fix-link-${s.n}`}
                          className="inline-flex items-center gap-1.5 text-sm font-bold text-brand hover:text-brand-hover transition-colors"
                        >
                          Open Step {s.n} <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => toggle(s.n)}
                          data-testid={`fix-done-${s.n}`}
                          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-bold transition-colors ${
                            isDone(s.n) ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                          }`}
                        >
                          {isDone(s.n) ? <><CheckCircle2 className="w-4 h-4" /> Done</> : <><Circle className="w-4 h-4" /> Mark done</>}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-10 mt-12 border-l-2 border-brand pl-6">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">Your priority next steps</div>
              <ul className="space-y-2">
                {ai_analysis.next_steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-slate-800 font-medium">
                    <span className="font-mono text-brand">0{i + 1}</span> {s}
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {PLAYBOOK_STEPS.map((r) => (
                <motion.div
                  key={r.n}
                  whileHover={{ y: -4 }}
                  className="bg-white border border-slate-200 rounded-md p-6 hover:border-brand transition-colors"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs tracking-[0.15em] uppercase font-bold text-slate-400">{r.tag}</span>
                    <span className="font-mono text-2xl font-bold tabular text-slate-200">{String(r.n).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{r.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{r.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {!qualified && (
        <section className="bg-slate-900 text-white" data-testid="developing-strategy-cta">
          <div className="max-w-5xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-xl">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Don't do it alone</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">
                Want {STRATEGIST.name.split(" ")[0]} to help you pick profitable lanes from day one?
              </h2>
              <p className="mt-3 text-slate-300">
                Close your gaps with the Guide, and book a lane strategy session so your first loads actually make money.
              </p>
            </div>
            <div className="shrink-0">
              <StrategyCallDialog source="strategy_diagnostic_developing" triggerClassName="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold" />
            </div>
          </div>
        </section>
      )}

      <div className="max-w-6xl mx-auto px-6 py-12 flex justify-center">
        <Button data-testid="restart-button" variant="ghost" onClick={onRestart} className="text-slate-500 hover:text-slate-900">
          <RotateCcw className="w-4 h-4 mr-2" /> Retake the diagnostic
        </Button>
      </div>
    </div>
  );
}
